/**
 * ORTHO constraint (AutoCAD `ORTHOMODE` / F8) — pure geometry helper.
 *
 * When Ortho mode is on, AutoCAD constrains cursor movement to the horizontal
 * or vertical axis of the current UCS RELATIVE TO THE LAST ACQUIRED POINT
 * (`$LASTPOINT` — the draft's rubber-band anchor). The cursor "sticks" to
 * whichever cardinal axis it is currently closer to: dragging mostly sideways
 * locks the segment to horizontal (Δy = 0), dragging mostly up/down locks it to
 * vertical (Δx = 0). Ortho does nothing until an anchor exists (it never affects
 * the FIRST point of a command — there is no reference point to lock against).
 *
 * This editor works in a 2D world where the drafting UCS axes coincide with the
 * world/model X and Y axes (no rotated UCS in scope), so the horizontal and
 * vertical locks are along world X and world Y. In a floating MSPACE draft the
 * anchor and cursor are BOTH expressed in the viewport's MODEL coordinates, so
 * the lock is along the model axes there too (independent of any viewport
 * twist) — matching AutoCAD, which aligns Ortho to the UCS, not the screen.
 *
 * Precedence: object snap OVERRIDES Ortho in AutoCAD — when a running OSNAP is
 * engaged the point goes to the snapped geometry, NOT the Ortho axis. The caller
 * (`stageInteractions`) therefore only applies this constraint when no OSNAP hit
 * is present.
 *
 * References:
 *   - Autodesk: ORTHOMODE (System Variable) — 0 = off, 1 = on; toggled by F8.
 *   - Autodesk: "To Constrain Cursor Movement to Right Angles (Ortho Mode)" —
 *     "the cursor can move only horizontally or vertically relative to the last
 *     point and the current UCS".
 *   - ezdxf: `$ORTHOMODE` header variable round-trips this session flag.
 *
 * THREE-free, dependency-light leaf — unit-testable in isolation and imported by
 * the pointer-input seam only.
 */

export interface OrthoPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Project `cursor` onto the horizontal or vertical axis through `base`,
 * whichever the cursor's direction is closer to. AutoCAD Ortho parity: the axis
 * with the larger absolute delta wins; a tie (|Δx| === |Δy|, including the
 * degenerate cursor-at-base case) locks to horizontal, matching AutoCAD's bias
 * toward the X axis at exact 45°.
 *
 *   |Δx| ≥ |Δy|  → horizontal segment: keep `cursor.x`, snap y to `base.y`
 *   |Δx| <  |Δy| → vertical   segment: snap x to `base.x`, keep `cursor.y`
 */
export function applyOrthoConstraint(
  base: OrthoPoint,
  cursor: OrthoPoint,
): { x: number; y: number } {
  const dx = cursor.x - base.x;
  const dy = cursor.y - base.y;
  if (Math.abs(dx) >= Math.abs(dy)) {
    // Horizontal lock: the segment runs along world/model X from the anchor.
    return { x: cursor.x, y: base.y };
  }
  // Vertical lock: the segment runs along world/model Y from the anchor.
  return { x: base.x, y: cursor.y };
}
